/**
 * Parses a coordinate string in one of three formats and returns
 * { lat, lng } in WGS84 decimal degrees, or null on failure.
 *
 * Supported input formats:
 *   1. WGS84 Dezimal  – "47.497913, 8.723582"  (Trennzeichen: , ; Leerzeichen)
 *   2. WGS84 DMS      – "47°29'52.5\"N 8°43'24.9\"E"
 *   3. LV95           – "2699163, 1261335"  (E 2'480'000–2'850'000 / N 1'070'000–1'300'000)
 *
 * Intern wird immer WGS84 Dezimal zurückgegeben.
 */

// ── helpers ──────────────────────────────────────────────────────────────────

/** Entfernt Tausender-Apostrophe, Kommas in grossen Ganzzahlen und normalisiert. */
function normalise(str) {
  return str
    .trim()
    .replace(/['\u2019]/g, "") // Apostrophe (Tausendertrenner LV95)
    .replace(/\u00B0/g, "°"); // vereinheitlicht Gradsymbol
}

// ── 1. WGS84 Dezimal ─────────────────────────────────────────────────────────

/**
 * Erkennt: "47.497913, 8.723582" | "47.497913 8.723582" | "47.497913;8.723582"
 * Auch mit führendem N/S/E/W und negativen Werten.
 */
function parseDecimal(s) {
  // optionales N/S/E/W vor oder nach der Zahl
  const num = "([+-]?\\d{1,3}(?:[.,]\\d+)?)";
  const dir = "[NSEWnsew]?";
  const sep = "[\\s,;/]+";
  const re = new RegExp(
    `^${dir}\\s*${num}°?\\s*${dir}${sep}${dir}\\s*${num}°?\\s*${dir}$`,
  );
  const m = s.match(re);
  if (!m) return null;

  let a = parseFloat(m[1].replace(",", "."));
  let b = parseFloat(m[2].replace(",", "."));
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  // Reihenfolge: lat ist der kleinere Absolutwert (Schweiz lat ~47, lng ~8–10)
  // Wenn beide plausibel als lat/lng, Ersteres als lat behandeln.
  // Vertauscht, wenn b im Breitenbereich und a im Längenbereich liegt.
  if (Math.abs(b) <= 90 && Math.abs(a) > 90) {
    [a, b] = [b, a];
  }

  if (!isValidWgs84(a, b)) return null;
  return { lat: round(a), lng: round(b) };
}

// ── 2. WGS84 DMS ─────────────────────────────────────────────────────────────

function dmsToDecimal(deg, min, sec, dir) {
  let d = Math.abs(deg) + min / 60 + sec / 3600;
  if (dir === "S" || dir === "W" || dir === "s" || dir === "w") d = -d;
  if (deg < 0) d = -Math.abs(d);
  return d;
}

/**
 * Erkennt z.B.:
 *   47°29'52.5"N 8°43'24.9"E
 *   N 47° 29' 52.5" E 8° 43' 24.9"
 *   47°29'52"N, 8°43'24"E
 */
function parseDMS(s) {
  // Normalisiert Anführungszeichen und Minuten-/Sekundenzeichen
  const clean = s
    .replace(/[''ʼ´`]/g, "'")
    .replace(/[""″]/g, '"')
    .replace(/\s*°\s*/g, "° ")
    .replace(/\s*'\s*/g, "' ")
    .replace(/\s*"\s*/g, '" ');

  // Einzel-Koordinate: Grad°Min'Sek"[NSEW]  oder  [NSEW]Grad°Min'Sek"
  const part =
    "([NSEWnsew])?\\s*(\\d{1,3})°\\s*(\\d{1,2})'\\s*(\\d{1,2}(?:[.,]\\d+)?)\"?\\s*([NSEWnsew])?";
  const re = new RegExp(`^${part}[,;\\s/]+${part}$`);
  const m = clean.match(re);
  if (!m) return null;

  const dir1 = (m[1] || m[5] || "").toUpperCase();
  const dir2 = (m[6] || m[10] || "").toUpperCase();

  const raw1 = dmsToDecimal(
    parseInt(m[2], 10),
    parseInt(m[3], 10),
    parseFloat(m[4].replace(",", ".")),
    dir1,
  );
  const raw2 = dmsToDecimal(
    parseInt(m[7], 10),
    parseInt(m[8], 10),
    parseFloat(m[9].replace(",", ".")),
    dir2,
  );

  if (!Number.isFinite(raw1) || !Number.isFinite(raw2)) return null;

  // lat = kleinerer Wert (Mitteleuropa: lat ~47, lng ~8)
  let lat = raw1,
    lng = raw2;
  if (Math.abs(raw1) > 90 || dir1 === "E" || dir1 === "W") {
    [lat, lng] = [raw2, raw1];
  }

  if (!isValidWgs84(lat, lng)) return null;
  return { lat: round(lat), lng: round(lng) };
}

// ── 3. LV95 → WGS84 ──────────────────────────────────────────────────────────

/**
 * Konvertiert LV95-Koordinaten (Landesvermessung 1995) nach WGS84.
 * Näherungsformel von swisstopo (Genauigkeit ~1 m).
 * E: 2'480'000 – 2'850'000 / N: 1'070'000 – 1'300'000
 */
function lv95ToWgs84(E, N) {
  const y = (E - 2_600_000) / 1_000_000;
  const x = (N - 1_200_000) / 1_000_000;

  const lng =
    2.6779094 +
    4.728982 * y +
    0.791484 * y * x +
    0.1306 * y * x * x -
    0.0436 * y * y * y;

  const lat =
    16.9023892 +
    3.238272 * x -
    0.270978 * y * y -
    0.002528 * x * x -
    0.0447 * y * y * x -
    0.014 * x * x * x;

  return {
    lat: round((lat * 100) / 36),
    lng: round((lng * 100) / 36),
  };
}

function isLv95(a, b) {
  // LV95: E 2'480'000–2'850'000, N 1'070'000–1'300'000
  const isE = a >= 2_480_000 && a <= 2_850_000;
  const isN = b >= 1_070_000 && b <= 1_300_000;
  return isE && isN;
}

function parseLv95(s) {
  // Entferne Tausender-Separatoren (Apostroph, Leerzeichen) und parse zwei Zahlen
  const clean = s.replace(/['\u2019\s]/g, (ch, _idx, str) => {
    // Apostroph immer entfernen; Leerzeichen nur wenn zwischen Ziffern
    return ch === "'" || ch === "\u2019" ? "" : " ";
  });

  const re = /^(-?\d+(?:[.,]\d+)?)[,;\s/]+(-?\d+(?:[.,]\d+)?)$/;
  const m = clean.trim().match(re);
  if (!m) return null;

  const a = parseFloat(m[1].replace(",", "."));
  const b = parseFloat(m[2].replace(",", "."));
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  // Prüfe ob E,N oder N,E Reihenfolge
  if (isLv95(a, b)) return lv95ToWgs84(a, b);
  if (isLv95(b, a)) return lv95ToWgs84(b, a);
  return null;
}

// ── Hilfsfunktionen ───────────────────────────────────────────────────────────

function isValidWgs84(lat, lng) {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

function round(v) {
  return Math.round(v * 1_000_000) / 1_000_000;
}

// ── Öffentliche API ───────────────────────────────────────────────────────────

/**
 * Parst einen Koordinaten-String und gibt das erkannte Format und
 * die WGS84-Dezimalwerte zurück.
 *
 * @param {string} input
 * @returns {{ lat: number, lng: number, format: 'decimal'|'dms'|'lv95' } | null}
 */
export function parseCoordinates(input) {
  if (!input || typeof input !== "string") return null;
  const s = normalise(input);

  // LV95 zuerst prüfen (große Ganzzahlen, eindeutig)
  const lv = parseLv95(s);
  if (lv) return { ...lv, format: "lv95" };

  // DMS prüfen (enthält °)
  if (s.includes("°")) {
    const dms = parseDMS(s);
    if (dms) return { ...dms, format: "dms" };
  }

  // Dezimal
  const dec = parseDecimal(s);
  if (dec) return { ...dec, format: "decimal" };

  return null;
}

/** Menschenlesbare Formatbezeichnung */
export function formatLabel(format) {
  switch (format) {
    case "decimal":
      return "WGS84 Dezimal";
    case "dms":
      return "WGS84 Grad/Min/Sek";
    case "lv95":
      return "LV95 (Schweizer Landeskoordinaten)";
    default:
      return "";
  }
}
